import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDashboardStats, getGapAnalysis } from '../../services/dashboardService'
import { getDatasetAnalytics } from '../../services/datasetService.jsx'
import axiosInstance from '../../services/axiosInstance'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ComposedChart, Line } from 'recharts'
import DataToolTip from '../../components/common/DataToolTip';
import Button from '../../components/common/Button'
import SyncAccuracyModal from '@/components/modals/SyncAccuracyModal'
import OccupationDetailModal from '@/components/modals/OccupationDetailModal'
import AreaExplorerModal from '@/components/modals/AreaExplorerModal'
import RegionalOccupationModal from '@/components/modals/RegionalOccupationModal'
import * as XLSX from 'xlsx'
import Papa from 'papaparse'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { getMsaRecords, getBosRecords, getNationalRecords, getStateRecords } from '../../services/datasetService.jsx'
import {
  BarChart3,
  LayoutDashboard,
  Download,
  Database,
  FileText
} from 'lucide-react'
import './styles.scss'
import { formatTimestamp, formatDate } from '../../utils/dateUtils'

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function WorkforceAnalytics() {
  const [stats, setStats] = useState({
    demandCount: 0,
    supplyCount: 0,
    msaOccupationCount: 0,
    bosOccupationCount: 0,
    nationalOccupationCount: 0,
    stateOccupationCount: 0,
    syncAccuracy: 0,
    activeYear: '2025',
    orphanedClusters: []
  })
  const [sessionYear, setSessionYear] = useState('2025')
  const oewsYear = stats.oewsYear || '2024'
  const [gapData, setGapData] = useState([])
  const [msaData, setMsaData] = useState([])
  const [geoData, setGeoData] = useState({
    states: [],
    areas: 0,
    primaryArea: '',
    avgWage: 0,
    stateCount: 0,
    topOcc: '',
    areaBreakdown: []
  })
  const [bosData, setBosData] = useState({
    totalRecords: 0,
    uniqueAreas: 0,
    uniqueNaics: 0,
    uniqueOccCodes: 0,
    medianWage: 0,
    naicsBreakdown: [],
    wagePercentiles: [],
    registry: []
  })
  const [natData, setNatData] = useState({
    totalRecords: 0,
    uniqueAreas: 0,
    uniqueNaics: 0,
    uniqueOccCodes: 0,
    medianWage: 0,
    naicsBreakdown: [],
    wagePercentiles: [],
    registry: []
  })
  const [stateData, setStateData] = useState({
    totalRecords: 0,
    uniqueAreas: 0,
    uniqueNaics: 0,
    uniqueOccCodes: 0,
    medianWage: 0,
    naicsBreakdown: [],
    wagePercentiles: [],
    states: [],
    wageLevels: [],
    stateCompWages: [],
    registry: []
  })
  const [areaSearch, setAreaSearch] = useState('')
  const [occSearch, setOccSearch] = useState('')
  const [activeTab, setActiveTab] = useState('demand-supply')
  const [loading, setLoading] = useState(true)
  const [selectedOcc, setSelectedOcc] = useState(null)
  const [isOccModalOpen, setIsOccModalOpen] = useState(false)
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false)
  const [isRegionalModalOpen, setIsRegionalModalOpen] = useState(false)
  const [selectedRegionalArea, setSelectedRegionalArea] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab]);
  const [showAccuracyModal, setShowAccuracyModal] = useState(false)
  const navigate = useNavigate()

  const getStrategyTooltip = (label) => {
    if (label.includes('Critical')) return 'High Shortage (>20%) + High Wage (>$48k). Action: Expand program capacity immediately.';
    if (label.includes('Growth')) return 'High Shortage (>20%) + Mid Wage ($35k-$48k). Action: Build talent pipeline for growing sectors.';
    if (label.includes('Saturation')) return 'Oversupply (<0%) + Low Wage (<$48k). Action: Review curriculum or refocus resources to higher-value fields.';
    if (label.includes('High Supply')) return 'High Supply (Oversupply) but High Wage (>$48k). Action: Graduates are finding high-value jobs; maintain volume.';
    return 'Supply and Demand are balanced. Action: Maintain current enrollment.';
  };

  const handleOccClick = (occ) => {
    setSelectedOcc(occ);
    setIsOccModalOpen(true);
  };

  const handleAreaClick = (areaName) => {
    setSelectedRegionalArea(areaName);
    setIsRegionalModalOpen(true);
  };

  const groupSlices = (data, thresholdPercent = 1) => {
    if (!data || data.length === 0) return [];
    
    const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
    if (totalValue === 0) return data;

    const sorted = [...data].sort((a, b) => (b.value || 0) - (a.value || 0));
    
    const aboveThreshold = [];
    const belowThreshold = [];
    
    sorted.forEach(item => {
      const share = ((item.value || 0) / totalValue) * 100;
      if (share >= thresholdPercent) {
        aboveThreshold.push(item);
      } else {
        belowThreshold.push(item);
      }
    });

    if (belowThreshold.length === 0) return aboveThreshold;

    const othersValue = belowThreshold.reduce((sum, item) => sum + (item.value || 0), 0);
    return [...aboveThreshold, { name: `Others (${belowThreshold.length} regions)`, value: othersValue }];
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      const [statsRes, gapRes, msaAnalytics, bosAnalytics, natAnalytics, stateAnalytics] = await Promise.all([
        getDashboardStats(sessionYear),
        getGapAnalysis(sessionYear),
        getDatasetAnalytics('msa', sessionYear),
        getDatasetAnalytics('bos', sessionYear),
        getDatasetAnalytics('national', sessionYear),
        getDatasetAnalytics('state', sessionYear)
      ])
      setStats(statsRes)
      setGapData(gapRes)

      // ── MSA Regional & Geographic ──────────────────────────────────────────
      setMsaData(msaAnalytics.registry || []);
      setGeoData({
        states: msaAnalytics.stateBreakdown || [],
        areas: msaAnalytics.uniqueAreas,
        primaryArea: msaAnalytics.registry?.[0]?.areaTitle || 'Memphis Regional Footprint',
        avgWage: msaAnalytics.medianWage,
        stateCount: msaAnalytics.stateBreakdown?.length || 0,
        topOcc: msaAnalytics.registry?.[0]?.occTitle || 'N/A',
        areaBreakdown: msaAnalytics.areaBreakdown || []
      });

      // ── BOS National OEWS ───────────────────────────────────────────
      setBosData({
        totalRecords: bosAnalytics.totalRecords,
        uniqueAreas: bosAnalytics.uniqueAreas,
        uniqueNaics: bosAnalytics.uniqueNaics,
        uniqueOccCodes: bosAnalytics.uniqueOccCodes,
        medianWage: bosAnalytics.medianWage,
        naicsBreakdown: bosAnalytics.naicsBreakdown || [],
        wagePercentiles: bosAnalytics.wagePercentiles || [],
        registry: bosAnalytics.registry || [],
        areaBreakdown: bosAnalytics.areaBreakdown || []
      });

      // ── NAT Industry ───────────────────────────────────────────
      setNatData({
        totalRecords: natAnalytics.totalRecords,
        uniqueAreas: natAnalytics.uniqueAreas,
        uniqueNaics: natAnalytics.uniqueNaics,
        uniqueOccCodes: natAnalytics.uniqueOccCodes,
        medianWage: natAnalytics.medianWage,
        naicsBreakdown: natAnalytics.naicsBreakdown,
        wagePercentiles: natAnalytics.wagePercentiles,
        registry: natAnalytics.registry,
        areaBreakdown: natAnalytics.areaBreakdown || []
      });

      // ── STATE Metrics ───────────────────────────────────────────
      setStateData({
        totalRecords: stateAnalytics.totalRecords,
        uniqueAreas: stateAnalytics.uniqueAreas,
        uniqueNaics: stateAnalytics.uniqueNaics,
        uniqueOccCodes: stateAnalytics.uniqueOccCodes,
        medianWage: stateAnalytics.medianWage,
        naicsBreakdown: stateAnalytics.naicsBreakdown,
        wagePercentiles: stateAnalytics.wagePercentiles,
        registry: stateAnalytics.registry,
        states: stateAnalytics.stateBreakdown || [],
        wageLevels: stateAnalytics.wageLevelBreakdown || [],
        stateCompWages: stateAnalytics.stateCompWages || [],
        areaBreakdown: stateAnalytics.areaBreakdown || []
      });

      setLoading(false)
    }
    fetchData()
  }, [sessionYear, oewsYear])

  const handleExportCSV = () => {
    const exportData = gapData.map(item => ({
      'Cluster Segment': item.cluster,
      'Demand (Job Openings)': item.demandTotal,
      'Supply (Graduates)': item.supplyTotal,
      'Gap (Shortage)': item.gap,
      'Avg Wage (Entry)': item.averageWage,
      'Decision Intelligence': item.insightLabel,
      'Intensity (%)': item.gapPercent.toFixed(2)
    }))
    const csv = Papa.unparse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', `Workforce_Gaps_${formatDate()}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleExportExcel = () => {
    const exportData = gapData.map(item => ({
      'Cluster Segment': item.cluster,
      'Demand (Job Openings)': item.demandTotal,
      'Supply (Graduates)': item.supplyTotal,
      'Gap (Shortage)': item.gap,
      'Avg Wage (Entry)': item.averageWage,
      'Decision Intelligence': item.insightLabel,
      'Intensity (%)': item.gapPercent.toFixed(2)
    }))
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Workforce Gaps')
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Workforce Gaps')
    XLSX.writeFile(workbook, `Workforce_Gaps_${formatDate()}.xlsx`)
  }

  const handleExportPDF = () => {
    const doc = new jsPDF()

    // Header & Summary Node
    doc.setFontSize(22)
    doc.setTextColor(30, 41, 59)
    doc.text('Strategic Workforce Intelligence Report', 14, 22)

    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text(`Regional Session: ${geoData.primaryArea}`, 14, 30)
    doc.text(`Generated on: ${formatTimestamp(new Date())}`, 14, 35)

    // --- SECTION 1: REGIONAL GEOGRAPHIC INTELLIGENCE (NEW) ---
    doc.setFontSize(14)
    doc.setTextColor(59, 130, 246)
    doc.text('I. Regional Geographic Footprint Summary', 14, 48)

    const geoSummary = [
      ["Principal Economic Center", geoData.primaryArea],
      ["Unique Areas Audited", `${geoData.areas} Sectors`],
      ["Avg Regional Wage Hub", `$${Math.round(geoData.avgWage).toLocaleString()}`],
      ["Multistate Coverage Node", `${geoData.stateCount} Districts identified`],
      ["Dominant Career Cluster", geoData.topOcc],
      ["Total Registry Density", `${msaData.length.toLocaleString()} Career Records`]
    ]

    autoTable(doc, {
      body: geoSummary,
      startY: 55,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold', width: 60 } }
    })

    // --- SECTION 2: CRITICAL WORKFORCE GAPS ---
    doc.setFontSize(14)
    doc.setTextColor(59, 130, 246)
    doc.text('II. Targeted Workforce Gap Analysis', 14, doc.lastAutoTable.finalY + 15)

    const tableColumn = ["Cluster Segment", "Demand", "Supply", "Gap", "Avg Wage", "Strategy"]
    const tableRows = gapData.map(item => [
      item.cluster,
      item.demandTotal.toLocaleString(),
      item.supplyTotal.toLocaleString(),
      item.gap.toLocaleString(),
      `$${item.averageWage.toLocaleString()}`,
      item.insightLabel
    ])

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: doc.lastAutoTable.finalY + 22,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      styles: { fontSize: 8 }
    })

    doc.save(`Workforce_Report_${formatDate()}.pdf`)
  }

  const handleDownloadExecutiveReport = async () => {
    try {
      // Trigger secure backend PDF generation via axiosInstance to include headers
      const response = await axiosInstance.get(`/api/dashboard/report/executive?year=${sessionYear}`, {
        responseType: 'blob'
      });

      // Create a local blob URL and trigger the click
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Memphis_Workforce_Executive_Report.pdf');
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download executive report:', error);
    }
  };

  const getActiveDatasetType = () => {
    if (activeTab === 'msa') return 'msa';
    if (activeTab === 'bos') return 'bos';
    if (activeTab === 'national') return 'national';
    if (activeTab === 'state') return 'state';
    return 'msa';
  };

  const getActiveAreaBreakdown = () => {
    if (activeTab === 'msa') return geoData.areaBreakdown;
    if (activeTab === 'bos') return bosData.areaBreakdown;
    if (activeTab === 'national') return natData.areaBreakdown;
    if (activeTab === 'state') return stateData.areaBreakdown;
    return geoData.areaBreakdown || [];
  };

  const getActiveYearForTab = () => {
    if (activeTab === 'demand-supply') return sessionYear;
    return oewsYear;
  };

  const marketData = [
    { name: 'Workforce Demand', value: stats.demandCount, color: '#3b82f6' },
    { name: 'Education Supply', value: stats.supplyCount, color: '#10b981' }
  ]

  if (loading) return (
    <div className="dashboard-container dashboard-loading">
      <div className="animate-pulse dashboard-loading-text">
        Harmonizing Workforce Intelligence...
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <SyncAccuracyModal
        isOpen={showAccuracyModal}
        onClose={() => setShowAccuracyModal(false)}
        orphanedClusters={stats.orphanedClusters}
      />

      <OccupationDetailModal
        isOpen={isOccModalOpen}
        onClose={() => setIsOccModalOpen(false)}
        occupation={selectedOcc}
      />

      <AreaExplorerModal
        isOpen={isAreaModalOpen}
        onClose={() => setIsAreaModalOpen(false)}
        datasetType={getActiveDatasetType()}
        areaBreakdown={getActiveAreaBreakdown()}
        year={getActiveYearForTab()}
      />

      <RegionalOccupationModal
        isOpen={isRegionalModalOpen}
        onClose={() => setIsRegionalModalOpen(false)}
        areaName={selectedRegionalArea}
        datasetType={getActiveDatasetType()}
        year={getActiveYearForTab()}
      />

      <header className="page-header-premium">
        <div className="hero-section">
          <BarChart3 className="header-icon-large" />
          <div className="title-stack">
            <div className="session-selector-badge">
              <span className="badge-label">ACTIVE SESSION:</span>
              <select
                className="year-select-premium"
                value={sessionYear}
                onChange={(e) => setSessionYear(e.target.value)}
              >
                <option value="2025">2025 Session</option>
                <option value="2024">2024 Session</option>
              </select>
            </div>
            <h1 className="dashboard-title">
              Workforce Intelligence <br />
              <span className="text-gradient">& Regional Analytics</span>
            </h1>
            <p className="subtitle">
              Strategic analysis of Regional Supply, Industry Demand, and Multi-State Benchmarks.
            </p>
          </div>
        </div>

        <div className="hub-primary-actions">
          <Button
            variant="primary"
            onClick={handleDownloadExecutiveReport}
            className="btn-outstanding-hero"
            title="Download full Memphis regional executive summary"
          >
            <FileText size={20} />
            Download Executive Intelligence Summary
          </Button>
        </div>
      </header>

      <section className="stats-bar">
        <div className="stat-item">
          <DataToolTip text="Demand Nodes: Total Job Openings and occupational demand signals aggregated across the regional footprint. (SOC Coded)">
            <div className="stat-container-stack">
              <span className="stat-label">Demand Nodes ({sessionYear}) ⓘ</span>
              <span className="stat-value">{stats.demandCount.toLocaleString()}</span>
              <div className="stat-subtext-meta">Total Job Openings</div>
            </div>
          </DataToolTip>
        </div>
        <div className="stat-item">
          <DataToolTip text="Supply Nodes: Total local graduates and talent supply pipeline representing regional program completions. (CIP Coded)">
            <div className="stat-container-stack">
              <span className="stat-label">Supply Nodes ({sessionYear}) ⓘ</span>
              <span className="stat-value">{stats.supplyCount.toLocaleString()}</span>
              <div className="stat-subtext-meta">College Graduates</div>
            </div>
          </DataToolTip>
        </div>
        <div className="stat-item">
          <DataToolTip text="MSA = Metropolitan Statistical Area. A geographic region with a relatively high population density at its core and close economic ties throughout the area.">
            <div className="stat-container-stack">
              <span className="stat-label">MSA Records ({oewsYear}) ⓘ</span>
              <span className="stat-value">{stats.msaOccupationCount.toLocaleString()}</span>
              <div className="stat-subtext-meta">Regional Occupation Nodes</div>
            </div>
          </DataToolTip>
        </div>
        <div className="stat-item">
          <DataToolTip text="BOS = National Occupational Employment and Wage Statistics (OEWS). Contains aggregate nationwide employment estimates.">
            <div className="stat-container-stack">
              <span className="stat-label">BOS Records ({oewsYear}) ⓘ</span>
              <span className="stat-value">{stats.bosOccupationCount > 0 ? stats.bosOccupationCount.toLocaleString() : '—'}</span>
              <div className="stat-subtext-meta">National Occupation Nodes</div>
            </div>
          </DataToolTip>
        </div>
        <div className="stat-item">
          <DataToolTip text="NAT = National NAICS cross-industry dataset nodes.">
            <div className="stat-container-stack">
              <span className="stat-label">NAT Records ({oewsYear}) ⓘ</span>
              <span className="stat-value">{natData.totalRecords > 0 ? natData.totalRecords.toLocaleString() : '—'}</span>
              <div className="stat-subtext-meta">National NAICS Nodes</div>
            </div>
          </DataToolTip>
        </div>
        <div className="stat-item">
          <DataToolTip text="STATE = State-level OEWS dataset nodes.">
            <div className="stat-container-stack">
              <span className="stat-label">STATE Records ({oewsYear}) ⓘ</span>
              <span className="stat-value">{stateData.totalRecords > 0 ? stateData.totalRecords.toLocaleString() : '—'}</span>
              <div className="stat-subtext-meta">State OEWS Nodes</div>
            </div>
          </DataToolTip>
        </div>
        <div
          className="stat-item clickable-stat"
          onClick={() => setShowAccuracyModal(true)}
          title="Click to audit unmatched clusters"
        >
          <span className="stat-label">Sync Accuracy</span>
          <span className="stat-value">{stats.syncAccuracy.toFixed(2)}%</span>
          <div className="stat-subtext-meta">Cluster Match Reliability (Audit)</div>
        </div>
        <div className="stat-item stat-badge-item">
          <DataToolTip text="Tennessee Regional Dataset: The authoritative workforce data source node currently active for this Memphis MSA analysis session. Calibrated for State standards.">
            <div className="stat-container-stack stat-container-stack--center">
              <div className="tn-badge">TENNESSEE REGIONAL DATASET ({oewsYear}) ⓘ</div>
              <div className="tn-sub-badge">Memphis Metro High Wage floor: $48k</div>
            </div>
          </DataToolTip>
        </div>
      </section>

      {/* --- DASHBOARD NAVIGATION HUB --- */}
      <div className="tab-navigation-wrapper animate-fade-in">
        <div className="tabs-pill">
          <button
            onClick={() => setActiveTab('demand-supply')}
            className={`tab-btn ${activeTab === 'demand-supply' ? 'tab--active' : ''}`}
          >
            📊 Demand & Supply ({sessionYear})
          </button>
          <button
            onClick={() => setActiveTab('msa')}
            className={`tab-btn ${activeTab === 'msa' ? 'tab--active' : ''}`}
          >
            <DataToolTip text={`Metropolitan Statistical Area Analysis (OES ${oewsYear})`} cursor="pointer">
              <span>🏢 MSA ({oewsYear})</span>
            </DataToolTip>
          </button>
          <button
            onClick={() => setActiveTab('bos')}
            className={`tab-btn ${activeTab === 'bos' ? 'tab--active' : ''}`}
          >
            <DataToolTip text={`Bureau of Labor Statistics National OEWS Dataset (${oewsYear}) — All US metro areas with NAICS industry breakdown`} cursor="pointer">
              <span>🌐 BOS ({oewsYear})</span>
            </DataToolTip>
          </button>
          <button
            onClick={() => setActiveTab('national')}
            className={`tab-btn ${activeTab === 'national' ? 'tab--active' : ''}`}
          >
            <DataToolTip text={`National Application Industry Segmentation and Wage Data (${oewsYear})`} cursor="pointer">
              <span>🇺🇸 NAT ({oewsYear})</span>
            </DataToolTip>
          </button>
          <button
            onClick={() => setActiveTab('state')}
            className={`tab-btn ${activeTab === 'state' ? 'tab--active' : ''}`}
          >
            <DataToolTip text={`State Occupational Employment and Wage Statistics (${oewsYear})`} cursor="pointer">
              <span>🗺️ STATE ({oewsYear})</span>
            </DataToolTip>
          </button>
        </div>
      </div>

      <section className="analytics-grid">
        {activeTab === 'bos' && (
          <>
            {/* BOS CARD 1: National Coverage Stats */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="card-header">
                <h3 className="card-title">National Coverage Overview</h3>
                <p className="card-subtitle">BOS National OEWS {oewsYear} — Occupational Employment Statistics across all US Metropolitan Areas</p>
              </div>
              <div className="geo-stats-summary-grid">
                <div className="geo-stat-node">
                  <DataToolTip text="Total number of distinct occupational records in the BOS national OEWS dataset ingested into the system.">
                    <span className="geo-stat-label-node">Total BOS Records ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-indigo">
                    {bosData.totalRecords > 0 ? bosData.totalRecords.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node clickable-stat" onClick={() => setIsAreaModalOpen(true)}>
                  <DataToolTip text="Number of unique metropolitan and nonmetropolitan areas covered in this national OEWS dataset. Click to explore areas and drill down.">
                    <span className="geo-stat-label-node">Areas Covered ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-emerald">
                    {bosData.uniqueAreas > 0 ? bosData.uniqueAreas.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Number of unique NAICS industry codes present in this dataset, enabling cross-sector wage analysis.">
                    <span className="geo-stat-label-node">NAICS Sectors ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-amber">
                    {bosData.uniqueNaics > 0 ? bosData.uniqueNaics.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Total distinct SOC occupation codes tracked in this national dataset across all industries and geographies.">
                    <span className="geo-stat-label-node">Occupation Codes ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-pink">
                    {bosData.uniqueOccCodes > 0 ? bosData.uniqueOccCodes.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Average annual median wage across all BOS occupational records in the 2024 OEWS dataset.">
                    <span className="geo-stat-label-node">Avg Median Wage ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-label-violet">
                    {bosData.medianWage > 0 ? `$${bosData.medianWage.toLocaleString()}` : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Reference year for this national occupational employment and wage statistics dataset.">
                    <span className="geo-stat-label-node">Dataset Year</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--dark">May {oewsYear}</div>
                </div>
              </div>
            </div>



            {/* BOS CARD 3: Wage Percentile Distribution */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="card-header">
                <h3 className="card-title">National Wage Percentile Distribution</h3>
                <p className="card-subtitle">Annual wage range from 10th to 90th percentile across top occupation groups (BOS 2024)</p>
              </div>
              <div className="chart-scroll-wrapper" style={{ overflow: 'hidden' }}>
                {bosData.wagePercentiles?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={450}>
                    <BarChart data={bosData.wagePercentiles} margin={{ top: 10, right: 20, left: 10, bottom: 140 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.15)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value, name) => [`$${value.toLocaleString()}`, name]} />
                      <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '20px' }} />
                      <Bar dataKey="p10" name="10th %ile" fill="#94a3b8" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p25" name="25th %ile" fill="#60a5fa" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="median" name="Median" fill="#6366f1" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p75" name="75th %ile" fill="#10b981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p90" name="90th %ile" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="bos-placeholder-notice">
                    <span className="bos-placeholder-icon">💰</span>
                    <div><strong>No wage percentile data available</strong><p>Upload <code>BOS_M2024_dl.xlsx</code> via the <a href="/datasets" className="bos-link">Datasets page</a> to see national wage distribution.</p></div>
                  </div>
                )}
              </div>
            </div>

            {/* BOS CARD 4: Occupation Registry */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.25s' }}>
              <div className="card-header card-header-flex card-header-flex--center">
                <div>
                  <h3 className="card-title">BOS Occupation Registry</h3>
                  <p className="card-subtitle">National OEWS occupational data — (Top 1000 highest-employment records)</p>
                </div>
                <div className="search-registry-wrapper">
                  <input
                    type="text"
                    placeholder="Search occupation or area..."
                    className="registry-search-input"
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                  />
                </div>
              </div>
              {bosData.registry.length > 0 ? (
                <div className="table-responsive">
                  <table className="data-table data-table--compact">
                    <thead>
                      <tr>
                        <th><div className="resizable-header">Area</div></th>
                        <th><div className="resizable-header">NAICS Industry</div></th>
                        <th><div className="resizable-header">Occupation</div></th>
                        <th className="text-right"><div className="resizable-header text-right" style={{ marginLeft: 'auto' }}>Employment</div></th>
                        <th className="text-right"><div className="resizable-header text-right" style={{ marginLeft: 'auto' }}>Median Wage</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bosData.registry
                        .filter(r =>
                          r.occTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.areaTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.naicsTitle?.toLowerCase().includes(areaSearch.toLowerCase())
                        )
                        .slice(0, 50) // showing top 50 matches
                        .map((r, idx) => (
                          <tr key={idx} onClick={() => handleOccClick(r)} className="interactive-row">
                            <td className="truncate-cell max-w-sm" title={r.areaTitle}>{r.areaTitle}</td>
                            <td className="truncate-cell max-w-sm" title={r.naicsTitle || r.naics}>{r.naicsTitle || r.naics}</td>
                            <td className="font-medium truncate-cell max-w-md" title={r.occTitle}>{r.occTitle} <span className="occ-code-badge">{r.occCode}</span></td>
                            <td className="text-right font-mono">{Number(r.totalEmployment || 0).toLocaleString()}</td>
                            <td className="text-right font-mono text-emerald">${Number(r.annualMedian || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bos-placeholder-notice">
                  <span className="bos-placeholder-icon">🌐</span>
                  <div>
                    <strong>BOS National Dataset Not Yet Loaded</strong>
                    <p>Upload <code>BOS_M2024_dl.xlsx</code> via the <a href="/datasets" className="bos-link">Datasets page</a> to activate this registry with live national OEWS data.</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'national' && (
          <>
            {/* NAT CARD 1: National Coverage Stats */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="card-header">
                <h3 className="card-title">National NAICS Coverage Overview</h3>
                <p className="card-subtitle">National Industry Segmentation and Wage Data (May {oewsYear})</p>
              </div>
              <div className="geo-stats-summary-grid">
                <div className="geo-stat-node">
                  <DataToolTip text="Total number of distinct occupational records in the National dataset ingested into the system.">
                    <span className="geo-stat-label-node">Total NAT Records ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-indigo">
                    {natData.totalRecords > 0 ? natData.totalRecords.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node clickable-stat" onClick={() => setIsAreaModalOpen(true)}>
                  <DataToolTip text="Number of unique metropolitan and nonmetropolitan areas covered. Click to explore areas and drill down.">
                    <span className="geo-stat-label-node">Areas Covered ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-emerald">
                    {natData.uniqueAreas > 0 ? natData.uniqueAreas.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Number of unique NAICS industry codes present in this dataset.">
                    <span className="geo-stat-label-node">NAICS Sectors ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-amber">
                    {natData.uniqueNaics > 0 ? natData.uniqueNaics.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Total distinct SOC occupation codes tracked.">
                    <span className="geo-stat-label-node">Occupation Codes ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-pink">
                    {natData.uniqueOccCodes > 0 ? natData.uniqueOccCodes.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Average annual median wage across all records.">
                    <span className="geo-stat-label-node">Avg Median Wage ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-label-violet">
                    {natData.medianWage > 0 ? `$${natData.medianWage.toLocaleString()}` : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Reference year for this dataset.">
                    <span className="geo-stat-label-node">Dataset Year</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--dark">May {oewsYear}</div>
                </div>
              </div>
            </div>

            {/* NAT CARD 3: Wage Percentile Distribution */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="card-header">
                <h3 className="card-title">National NAICS Wage Percentile Distribution</h3>
                <p className="card-subtitle">Annual wage range from 10th to 90th percentile across top occupation groups</p>
              </div>
              <div className="chart-scroll-wrapper" style={{ overflow: 'hidden' }}>
                {natData.wagePercentiles.length > 0 ? (
                  <ResponsiveContainer width="100%" height={450}>
                    <BarChart data={natData.wagePercentiles} margin={{ top: 10, right: 20, left: 10, bottom: 140 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.15)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value, name) => [`$${value.toLocaleString()}`, name]} />
                      <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '20px' }} />
                      <Bar dataKey="p10" name="10th %ile" fill="#94a3b8" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p25" name="25th %ile" fill="#60a5fa" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="median" name="Median" fill="#6366f1" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p75" name="75th %ile" fill="#10b981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p90" name="90th %ile" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="bos-placeholder-notice">
                    <span className="bos-placeholder-icon">💰</span>
                    <div><strong>No wage percentile data available</strong><p>Upload <code>national_M2024_dl.xlsx</code> via the <a href="/datasets" className="bos-link">Datasets page</a> to see national wage distribution.</p></div>
                  </div>
                )}
              </div>
            </div>

            {/* NAT CARD 4: Occupation Registry */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.25s' }}>
              <div className="card-header card-header-flex card-header-flex--center">
                <div>
                  <h3 className="card-title">National NAICS Occupation Registry</h3>
                  <p className="card-subtitle">Highest employment records — (Top 1000)</p>
                </div>
                <div className="search-registry-wrapper">
                  <input
                    type="text"
                    placeholder="Search occupation or area..."
                    className="registry-search-input"
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                  />
                </div>
              </div>
              {natData.registry.length > 0 ? (
                <div className="table-responsive">
                  <table className="data-table data-table--compact">
                    <thead>
                      <tr>
                        <th><div className="resizable-header">Area</div></th>
                        <th><div className="resizable-header">NAICS Industry</div></th>
                        <th><div className="resizable-header">Occupation</div></th>
                        <th className="text-right"><div className="resizable-header text-right" style={{ marginLeft: 'auto' }}>Employment</div></th>
                        <th className="text-right"><div className="resizable-header text-right" style={{ marginLeft: 'auto' }}>Median Wage</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {natData.registry
                        .filter(r =>
                          r.occTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.areaTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.naicsTitle?.toLowerCase().includes(areaSearch.toLowerCase())
                        )
                        .slice(0, 50)
                        .map((r, idx) => (
                          <tr key={idx} onClick={() => handleOccClick(r)} className="interactive-row">
                            <td className="truncate-cell max-w-sm" title={r.areaTitle}>{r.areaTitle}</td>
                            <td className="truncate-cell max-w-sm" title={r.naicsTitle || r.naics}>{r.naicsTitle || r.naics}</td>
                            <td className="font-medium truncate-cell max-w-md" title={r.occTitle}>{r.occTitle} <span className="occ-code-badge">{r.occCode}</span></td>
                            <td className="text-right font-mono">{Number(r.totalEmployment || 0).toLocaleString()}</td>
                            <td className="text-right font-mono text-emerald">${Number(r.annualMedian || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bos-placeholder-notice">
                  <span className="bos-placeholder-icon">🌐</span>
                  <div>
                    <strong>National Dataset Not Yet Loaded</strong>
                    <p>Upload <code>national_M2024_dl.xlsx</code> via the <a href="/datasets" className="bos-link">Datasets page</a> to activate this registry.</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'state' && (
          <>
            {/* STATE CARD 1: State Coverage Stats */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="card-header">
                <h3 className="card-title">State OEWS Coverage Overview</h3>
                <p className="card-subtitle">State Occupational Employment and Wage Statistics (May 2024)</p>
              </div>
              <div className="geo-stats-summary-grid">
                <div className="geo-stat-node">
                  <DataToolTip text="Total number of distinct occupational records in the State dataset ingested into the system.">
                    <span className="geo-stat-label-node">Total STATE Records ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-indigo">
                    {stateData.totalRecords > 0 ? stateData.totalRecords.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node clickable-stat" onClick={() => setIsAreaModalOpen(true)}>
                  <DataToolTip text="Number of unique metropolitan and nonmetropolitan areas covered within the state dataset. Click to explore areas and drill down.">
                    <span className="geo-stat-label-node">Areas Covered ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-emerald">
                    {stateData.uniqueAreas > 0 ? stateData.uniqueAreas.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Number of unique NAICS industry codes present in this dataset.">
                    <span className="geo-stat-label-node">NAICS Sectors ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-amber">
                    {stateData.uniqueNaics > 0 ? stateData.uniqueNaics.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Total distinct SOC occupation codes tracked at the state level.">
                    <span className="geo-stat-label-node">Occupation Codes ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-pink">
                    {stateData.uniqueOccCodes > 0 ? stateData.uniqueOccCodes.toLocaleString() : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Average annual median wage across all records in the state-level 2024 OEWS dataset.">
                    <span className="geo-stat-label-node">Avg Median Wage ⓘ</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-label-violet">
                    {stateData.medianWage > 0 ? `$${stateData.medianWage.toLocaleString()}` : '—'}
                  </div>
                </div>
                <div className="geo-stat-node">
                  <DataToolTip text="Reference year for this state-level occupational employment and wage statistics dataset.">
                    <span className="geo-stat-label-node">Dataset Year</span>
                  </DataToolTip>
                  <div className="geo-stat-main-value geo-stat-main-value--dark">May 2024</div>
                </div>
              </div>
            </div>

            {/* STATE CARD 2: Geographic Distribution Hub */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <div className="card-header">
                <h3 className="card-title">State Workforce Distribution Hub</h3>
                <p className="card-subtitle">Comparative employment volume across state territories</p>
              </div>

              <div className="geo-hub-content">
                <div className="geo-viz-row">
                  <div className="geo-viz-chart" style={{ height: '320px', minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%" key={`state-dist-${activeTab}`}>
                      <PieChart>
                        <Pie
                          data={stateData.states}
                          innerRadius="65%"
                          outerRadius="90%"
                          paddingAngle={4}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          animationDuration={1000}
                        >
                          {stateData.states.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [value.toLocaleString(), `Employees in ${name}`]}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="geo-legend-sidebar">
                    <h4 className="geo-legend-title">Territory Distribution</h4>
                    <div className="legend-flex-stack">
                      {stateData.states.map((entry, index) => (
                        <div key={index} className="legend-item-row">
                          <div className="legend-marker-group">
                            <div className="legend-dot" style={{ background: COLORS[index % COLORS.length] }} />
                            <span className="legend-label-text">{entry.name}</span>
                          </div>
                          <span className="legend-value-text">{((entry.value / stateData.states.reduce((a, b) => a + b.value, 0)) * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STATE CARD 3: Wage Compensation Hub */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="card-header">
                <h3 className="card-title">State Wage Compensation Matrix</h3>
                <p className="card-subtitle">Workforce distribution across Entry, Professional, and Executive wage tiers</p>
              </div>

              <div className="geo-hub-content">
                <div className="geo-viz-row">
                  <div className="geo-viz-chart" style={{ height: '320px', minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%" key={`state-wage-tier-${activeTab}`}>
                      <PieChart>
                        <Pie
                          data={stateData.wageLevels}
                          innerRadius="65%"
                          outerRadius="90%"
                          paddingAngle={4}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          animationDuration={1000}
                        >
                          {stateData.wageLevels.map((entry, index) => (
                            <Cell key={`cell-wage-${index}`} fill={COLORS[(index + 3) % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [value.toLocaleString(), `Total in ${name}`]}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="geo-legend-sidebar">
                    <h4 className="geo-legend-title">Compensation Matrix</h4>
                    <div className="legend-flex-stack">
                      {stateData.wageLevels.map((entry, index) => (
                        <div key={index} className="legend-item-row">
                          <div className="legend-marker-group">
                            <div className="legend-dot" style={{ background: COLORS[(index + 3) % COLORS.length] }} />
                            <span className="legend-label-text">{entry.name}</span>
                          </div>
                          <span className="legend-value-text">
                            {((entry.value / Object.values(stateData.wageLevels).reduce((a, b) => a + b.value, 0)) * 100).toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STATE CARD 4: Wage Benchmarking Hub */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.25s' }}>
              <div className="card-header">
                <h3 className="card-title">State Wage Benchmarking Hub</h3>
                <p className="card-subtitle">Comparative average median wages across territorial jurisdictions</p>
              </div>

              <div className="geo-hub-content">
                <div className="geo-viz-row">
                  <div className="geo-viz-chart" style={{ height: `${Math.max(400, stateData.stateCompWages.length * 40)}px`, minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%" key={`state-bench-${activeTab}`}>
                      <BarChart
                        layout="vertical"
                        data={stateData.stateCompWages}
                        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }} />
                        <Tooltip
                          formatter={(value) => [`$${value.toLocaleString()}`, 'Avg Median Wage']}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        />
                        <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={20} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="geo-legend-sidebar" style={{ height: `${Math.max(400, stateData.stateCompWages.length * 40)}px` }}>
                    <h4 className="geo-legend-title">Wage Benchmarks</h4>
                    <div className="legend-flex-stack">
                      {stateData.stateCompWages.map((entry, index) => (
                        <div key={index} className="legend-item-row">
                          <div className="legend-marker-group">
                            <div className="legend-dot" style={{ background: '#6366f1', opacity: 1 - (index * 0.1) }} />
                            <span className="legend-label-text">{entry.name}</span>
                          </div>
                          <span className="legend-value-text" style={{ fontWeight: 700, color: '#6366f1' }}>
                            ${entry.value.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STATE CARD 5: Wage Percentile Distribution */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="card-header">
                <h3 className="card-title">State Occupation Wage Percentiles</h3>
                <p className="card-subtitle">Annual wage range from 10th to 90th percentile across top occupation groups (State 2024)</p>
              </div>
              <div className="chart-scroll-wrapper" style={{ overflow: 'hidden', minWidth: 0 }}>
                {stateData.wagePercentiles.length > 0 ? (
                  <ResponsiveContainer width="100%" height={450} key={`state-pcts-${activeTab}`}>
                    <BarChart data={stateData.wagePercentiles} margin={{ top: 10, right: 20, left: 10, bottom: 140 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.15)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value, name) => [`$${value.toLocaleString()}`, name]} />
                      <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '20px' }} />
                      <Bar dataKey="p10" name="10th %ile" fill="#94a3b8" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p25" name="25th %ile" fill="#60a5fa" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="median" name="Median" fill="#6366f1" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p75" name="75th %ile" fill="#10b981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="p90" name="90th %ile" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="bos-placeholder-notice">
                    <span className="bos-placeholder-icon">💰</span>
                    <div><strong>No wage percentile data available</strong><p>Upload <code>state_M2024_dl.xlsx</code> via the <a href="/datasets" className="bos-link">Datasets page</a> to see state-level wage distribution.</p></div>
                  </div>
                )}
              </div>
            </div>

            {/* STATE CARD 6: Occupation Registry */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.35s' }}>
              <div className="card-header card-header-flex card-header-flex--center">
                <div>
                  <h3 className="card-title">State Occupation Registry</h3>
                  <p className="card-subtitle">State OEWS occupational data — (Top 1000 highest-employment records)</p>
                </div>
                <div className="search-registry-wrapper">
                  <input
                    type="text"
                    placeholder="Search occupation or area..."
                    className="registry-search-input"
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                  />
                </div>
              </div>
              {stateData.registry.length > 0 ? (
                <div className="table-responsive">
                  <table className="data-table data-table--compact">
                    <thead>
                      <tr>
                        <th><div className="resizable-header">Area</div></th>
                        <th><div className="resizable-header">State</div></th>
                        <th><div className="resizable-header">NAICS Industry</div></th>
                        <th><div className="resizable-header">Occupation</div></th>
                        <th className="text-right"><div className="resizable-header text-right" style={{ marginLeft: 'auto' }}>Employment</div></th>
                        <th className="text-right"><div className="resizable-header text-right" style={{ marginLeft: 'auto' }}>Median Wage</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stateData.registry
                        .filter(r =>
                          r.occTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.areaTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.naicsTitle?.toLowerCase().includes(areaSearch.toLowerCase()) ||
                          r.primState?.toLowerCase().includes(areaSearch.toLowerCase())
                        )
                        .slice(0, 50)
                        .map((r, idx) => (
                          <tr key={idx} onClick={() => handleOccClick(r)} className="interactive-row">
                            <td className="truncate-cell max-w-sm" title={r.areaTitle}>{r.areaTitle}</td>
                            <td className="font-bold text-indigo" style={{ whiteSpace: 'nowrap' }}>{r.primState || '—'}</td>
                            <td className="truncate-cell max-w-sm" title={r.naicsTitle || r.naics}>{r.naicsTitle || r.naics}</td>
                            <td className="font-medium truncate-cell max-w-md" title={r.occTitle}>{r.occTitle} <span className="occ-code-badge">{r.occCode}</span></td>
                            <td className="text-right font-mono">{Number(r.totalEmployment || 0).toLocaleString()}</td>
                            <td className="text-right font-mono text-emerald">${Number(r.annualMedian || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bos-placeholder-notice">
                  <span className="bos-placeholder-icon">🗺️</span>
                  <div>
                    <strong>State Dataset Not Yet Loaded</strong>
                    <p>Upload <code>state_M2024_dl.xlsx</code> via the <a href="/datasets" className="bos-link">Datasets page</a> to activate this registry with live state-level OEWS data.</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'msa' && (
          <>
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="card-header">
                <h3 className="card-title">Regional Geographic Footprint</h3>
                <p className="card-subtitle">Workforce Distribution by Primary State & Area Coverage</p>
              </div>

              <div className="geo-hub-content">
                {/* --- ROW 1: PIE & SCROLLABLE LEGEND SIDEBAR --- */}
                <div className="geo-viz-row">
                  <div className="geo-viz-chart">
                    <ResponsiveContainer width="100%" height={400} minWidth={100} minHeight={100}>
                      <PieChart>
                        <Pie
                          data={groupSlices(geoData?.areaBreakdown || [], 0.5)}
                          innerRadius="65%"
                          outerRadius="90%"
                          paddingAngle={3}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          animationBegin={0}
                          animationDuration={800}
                          isAnimationActive={true}
                        >
                          {(geoData?.areaBreakdown || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => {
                            const total = geoData?.areaBreakdown?.reduce((sum, item) => sum + (item.value || 0), 0) || 1;
                            const percent = ((value || 0) / total * 100).toFixed(1);
                            return [`${value?.toLocaleString()} (${percent}%)`, `Employees in ${name || 'Unknown'}`];
                          }}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                            padding: '12px',
                            fontWeight: '600'
                          }}
                          itemStyle={{ color: '#6366f1' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="geo-legend-sidebar">
                    <h4 className="geo-legend-title">Regional Distribution Hub</h4>
                    <div className="legend-flex-stack">
                      {(geoData?.areaBreakdown || []).length > 0 ? (geoData.areaBreakdown.map((entry, index) => (
                        <div key={index} className="legend-item-row">
                          <div className="legend-marker-group">
                            <div className="legend-dot" style={{ background: COLORS[index % COLORS.length] }} />
                            <span className="legend-label-text">{entry.name}</span>
                          </div>
                          <span className="legend-value-text">{entry.value?.toLocaleString() || 0}</span>
                        </div>
                      ))) : (
                        <div className="empty-registry-state">No regional state data mapping available for this node.</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* --- ROW 2: STRATEGIC METADATA REGISTRY (2x3 GRID) --- */}
                <div className="geo-stats-summary-grid">
                  <div className="geo-stat-node">
                    <DataToolTip text="The primary metropolitan sector within this regional footprint possessing the highest aggregated employment volume.">
                      <span className="geo-stat-label-node">
                        Principal Economic Center ⓘ
                      </span>
                    </DataToolTip>
                    <div className="geo-stat-main-value geo-stat-main-value--dark">
                      {geoData.primaryArea}
                    </div>
                  </div>

                  <div className="geo-stat-node clickable-stat" onClick={() => setIsAreaModalOpen(true)}>
                    <DataToolTip text="Total number of distinct geographic subdivisions (MSA sectors/counties) successfully mapped. Click to explore areas and drill down.">
                      <span className="geo-stat-label-node">
                        Unique Areas Audited ⓘ
                      </span>
                    </DataToolTip>
                    <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-indigo">
                      {geoData.areas}
                    </div>
                  </div>

                  <div className="geo-stat-node">
                    <DataToolTip text="Total count of unique occupational clusters and career data nodes currently being harmonized for this regional session.">
                      <span className="geo-stat-label-node">
                        Total Registry Density ⓘ
                      </span>
                    </DataToolTip>
                    <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-emerald">
                      {msaData.length.toLocaleString()}
                    </div>
                  </div>

                  <div className="geo-stat-node">
                    <DataToolTip text="Calculated mean wage node across all audited regional subdivisions—identifying the definitive economic standard for your MSA footprint.">
                      <span className="geo-stat-label-node">
                        Avg Regional Wage ⓘ
                      </span>
                    </DataToolTip>
                    <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-amber">
                      ${Math.round(geoData.avgWage).toLocaleString()}
                    </div>
                  </div>

                  <div className="geo-stat-node">
                    <DataToolTip text="Total number of states (e.g., TN, MS, AR) successfully mapped and harmonized within this specific MSA region footprint.">
                      <span className="geo-stat-label-node">
                        Multi-State Footprint ⓘ
                      </span>
                    </DataToolTip>
                    <div className="geo-stat-main-value geo-stat-main-value--large geo-stat-label-pink">
                      {geoData.stateCount} <span className="geo-stat-sub-label">Districts</span>
                    </div>
                  </div>

                  <div className="geo-stat-node">
                    <DataToolTip text="The specific occupational cluster or career path possessing the highest workforce concentration across the current regional footprint.">
                      <span className="geo-stat-label-node">
                        Dominant Career Cluster ⓘ
                      </span>
                    </DataToolTip>
                    <div className="geo-stat-main-value geo-stat-label-violet">
                      {geoData.topOcc}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- CARD 2: MARKET SECTOR REGISTRY --- */}
            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <div className="card-header card-header-flex card-header-flex--center">
                <div>
                  <h3 className="card-title">Market Sector Registry (MSA Breakdown)</h3>
                  <p className="card-subtitle">Workforce Concentration across Metropolitan Statistical Areas & Surrounding Jurisdictions</p>
                </div>
                <div className="search-registry-wrapper">
                  <input
                    type="text"
                    placeholder="Search by Jurisdiction Sector..."
                    className="registry-search-input"
                    value={areaSearch}
                    onChange={(e) => setAreaSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="card-body card-body--padded">
                <div className="area-list-registry">
                  {(geoData?.areaBreakdown || [])
                    .filter(item => (item?.name || "").toLowerCase().includes((areaSearch || "").toLowerCase()))
                    .map((item, idx) => (
                      <div key={item?._id || idx} className="area-node-item clickable-node" onClick={() => handleAreaClick(item.name)}>
                        <div className="area-info">
                          <div className="area-label">Jurisdiction Sector</div>
                          <div className="area-name">{item.name}</div>
                        </div>
                        <div className="area-volume">
                          <div className="volume-value">{item.value.toLocaleString()}</div>
                          <div className="volume-label">Total Employment Volume</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="card-header card-header-flex card-header-flex--center">
                <div>
                  <h3 className="card-title">Regional Employment Density (2024)</h3>
                  <p className="card-subtitle">Top 1000 Occupations in Regional MSA by Total Employment (Scrollable)</p>
                </div>
                <div className="search-registry-wrapper">
                  <input
                    type="text"
                    placeholder="Search Occupations..."
                    className="registry-search-input"
                    value={occSearch}
                    onChange={(e) => setOccSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="chart-container-inner chart-scroll-wrapper" style={{ minWidth: 0 }}>
                {(() => {
                  const filteredOccupations = msaData.filter(r => 
                    (r.occTitle || "").toLowerCase().includes(occSearch.toLowerCase()) ||
                    (r.occCode || "").includes(occSearch)
                  );
                  return (
                    <div style={{ height: `${Math.max(filteredOccupations.length, 12) * 35}px`, minHeight: '400px' }}>
                      <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100}>
                        <BarChart data={filteredOccupations.slice(0, 500)} layout="vertical" margin={{ left: 100, right: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f0f0f0" />
                          <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 12 }} hide />
                          <YAxis
                            dataKey="id"
                            type="category"
                            tickFormatter={(id) => {
                              const occ = filteredOccupations.find(r => r.id === id);
                              return occ ? occ.occTitle : '';
                            }}
                            tick={{ fontSize: 10 }}
                            width={150}
                            stroke="#94a3b8"
                          />
                          <Tooltip
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                            labelFormatter={(id) => {
                              const occ = filteredOccupations.find(r => r.id === id);
                              return occ ? occ.occTitle : 'Unknown Occupation';
                            }}
                            formatter={(value, name, props) => {
                              const occ = filteredOccupations.find(r => r.id === props.payload.id);
                              return [
                                <span><strong>{value.toLocaleString()}</strong> <small style={{color: '#94a3b8'}}>(in {occ?.areaTitle || 'Unknown Area'})</small></span>,
                                'Total Employment'
                              ];
                            }}
                          />
                          <Bar 
                            dataKey="totalEmployment" 
                            name="Total Employment" 
                            fill="#6366f1" 
                            radius={[0, 4, 4, 0]} 
                            barSize={18} 
                            onClick={(data) => handleOccClick(data)}
                            style={{ cursor: 'pointer' }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>
          </>
        )}

        {activeTab === 'demand-supply' && (
          <>
            <div className="analytics-card card--full-width animate-fade-in">
              <div className="card-header">
                <h3 className="card-title">Market Proportion</h3>
                <p className="card-subtitle">Overall Demand vs Supply Ratio</p>
              </div>
              <div className="chart-container-inner">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={marketData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {marketData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry) => {
                        const total = marketData.reduce((acc, item) => acc + item.value, 0);
                        const percent = total > 0 ? (entry.payload.value / total * 100).toFixed(1) : 0;
                        return <span className="legend-text-node">{value}: {percent}%</span>;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="analytics-card card--full-width animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="card-header">
                <h3 className="card-title">Workforce Gap Insight</h3>
                <p className="card-subtitle">Comparing Demand (SOC) vs Supply (CIP) by Industry Cluster</p>
              </div>
              <div className="chart-container-inner">
                <ResponsiveContainer width="100%" height={450}>
                  <ComposedChart data={gapData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="cluster"
                      tick={{ fontSize: 10 }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      stroke="#94a3b8"
                    />
                    <YAxis
                      scale="log"
                      domain={[1, 'auto']}
                      allowDataOverflow={true}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                    />
                    <Legend verticalAlign="top" height={36} />
                    <Bar dataKey="demandTotal" name="Job Openings" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={25} />
                    <Bar dataKey="supplyTotal" name="Graduates" fill="#10b981" radius={[4, 4, 0, 0]} barSize={25} />
                    <Line type="monotone" dataKey="gap" name="Market Discrepancy" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </section>

      {activeTab === 'demand-supply' && gapData.length > 0 && (
        <div className="dashboard-content-wide animate-slide-up">
          <div className="analytics-card card--full-width">
            <div className="card-header card-header-flex">
              <div>
                <h2 className="card-title">Critical Workforce Gaps</h2>
                <div className="header-meta-group">
                  <p className="card-subtitle">Top segments where educational output is disconnected from regional demand.</p>
                  <span className="regional-benchmark-pill" title="Calibrated for Memphis/TN Workforce">
                    TN Benchmark: $48k
                  </span>
                </div>
              </div>
              <div className="table-export-actions">
                <button className="export-btn excel" title="Export to Excel" onClick={handleExportExcel}>
                  <span className="export-icon">📊</span>
                  <span className="export-label">XLSX</span>
                </button>
                <button className="export-btn csv" title="Export to CSV" onClick={handleExportCSV}>
                  <span className="export-icon">📄</span>
                  <span className="export-label">CSV</span>
                </button>
                <button className="export-btn pdf" title="Export to PDF" onClick={handleExportPDF}>
                  <span className="export-icon">📕</span>
                  <span className="export-label">PDF</span>
                </button>
              </div>
            </div>
            <div className="gaps-table-container custom-scrollbar">
              <table className="gaps-table">
                <thead>
                  <tr>
                    <th>Cluster Segment</th>
                    <th className="text-right">
                      Demand
                      <div className="table-header-subnode">Job Openings</div>
                    </th>
                    <th className="text-right">
                      Supply
                      <div className="table-header-subnode">Graduates</div>
                    </th>
                    <th className="text-right">
                      Gap
                      <div className="table-header-subnode">(Shortage)</div>
                    </th>
                    <th className="text-right">
                      Avg Wage
                      <div className="table-header-subnode">Entry Level</div>
                    </th>
                    <th className="text-center">Decision Strategy</th>
                    <th className="text-center">Intensity</th>
                  </tr>
                </thead>
                <tbody>
                  {gapData.map((item, idx) => {
                    const getIntensityMetadata = (percent) => {
                      const abs = Math.abs(percent);
                      if (percent < 0) return { class: 'surplus', label: `${abs.toFixed(0)}% Surplus`, textClass: 'text--surplus' };
                      if (percent > 70) return { class: 'critical', label: `${abs.toFixed(0)}% Shortage`, textClass: 'text--critical' };
                      if (percent > 30) return { class: 'significant', label: `${abs.toFixed(0)}% Shortage`, textClass: 'text--significant' };
                      return { class: 'tight', label: `${abs.toFixed(0)}% Shortage`, textClass: 'text--tight' };
                    };

                    const meta = getIntensityMetadata(item.gapPercent);

                    return (
                      <tr key={idx}>
                        <td className="cluster-name">{item.cluster}</td>
                        <td className="text-right">{item.demandTotal.toLocaleString()}</td>
                        <td className="text-right">{item.supplyTotal.toLocaleString()}</td>
                        <td className="text-right gap-value">{item.gap.toLocaleString()}</td>
                        <td className="text-right wage-value-mono">
                          ${item.averageWage.toLocaleString()}
                        </td>
                        <td className="text-center">
                          <span
                            className={`insight-badge ${item.insightLabel.includes('Critical') ? 'badge-critical' : item.insightLabel.includes('Growth') ? 'badge-growth' : item.insightLabel.includes('Saturation') ? 'badge-caution' : 'badge-aligned'}`}
                            data-tooltip={getStrategyTooltip(item.insightLabel)}
                          >
                            {item.insightLabel}
                          </span>
                        </td>
                        <td>
                          <div className="intensity-wrapper">
                            <div className="intensity-bar">
                              <div
                                className={`intensity-fill intensity--${meta.class}`}
                                style={{ width: `${Math.min(Math.max(Math.abs(item.gapPercent), 5), 100)}%` }}
                              ></div>
                            </div>
                            <span className={`intensity-label ${meta.textClass}`}>
                              {meta.label}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <section className="features-section">
        <div className="feature-card">
          <div className="feature-header">
            <span className="feature-icon">🔍</span>
            <h3 className="feature-title">Decision Intelligence</h3>
          </div>
          <p className="feature-desc">Identify high-potential career pathways by automatically connecting regional job market requirements with specific educational programs.</p>
        </div>
        <div className="feature-card">
          <div className="feature-header">
            <span className="feature-icon">📈</span>
            <h3 className="feature-title">Supply Capacity</h3>
          </div>
          <p className="feature-desc">Monitor regional talent production to ensure educational institutions are graduating enough students to meet critical workforce needs.</p>
        </div>
        <div className="feature-card">
          <div className="feature-header">
            <span className="feature-icon">🎯</span>
            <h3 className="feature-title">Strategic Sync</h3>
          </div>
          <p className="feature-desc">Surface critical workforce shortages instantly, allowing you to prioritize funding and resources where they will have the most impact.</p>
        </div>
      </section>
    </div>
  )
}
